import React, { createContext, use, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import { useGetBankAccountInfo } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'
import { useSelectConnection } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/SelectConnection.gql'
import { isBankInfoPopulated } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/utils'

export interface MXConnectionStatusContextValue {
  isLoadingNewAccount: boolean
  newBankAccountId: string | null
  setNewBankAccountId: (id: string) => void
}

export const MXConnectionStatusContext = createContext<MXConnectionStatusContextValue>({
  isLoadingNewAccount: false,
  newBankAccountId: null,
  setNewBankAccountId: () => {},
})

const MXConnectionStatusProvider = ({ children }: React.PropsWithChildren) => {
  const { appId } = useParams()
  const showToast = useToast()

  const [newBankAccountId, setNewBankAccountIdInternal] = useState<string | null>(null)
  const [isLoadingNewAccount, setIsLoadingNewAccount] = useState(false)

  const setNewBankAccountId = useCallback((id: string) => {
    setNewBankAccountIdInternal(id)
    setIsLoadingNewAccount(true)
  }, [])

  const [selectConnection] = useSelectConnection()

  const { data, error } = useGetBankAccountInfo({
    variables: {
      creditApplicationId: appId ?? '',
    },
    fetchPolicy: 'no-cache',
    pollInterval: 1000,
    skip: !appId || !isLoadingNewAccount || !newBankAccountId,
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to fetch updated external bank information'
          id='sputnik.MXConnectionStatusProvider__bXnZcL'
        />
      ),
    },
    customAction: () => {
      setIsLoadingNewAccount(false)
    },
  })

  useEffect(() => {
    if (!data?.creditApplication?.financeInfo?.externalBankOptions) {
      return
    }

    const { externalBankOptions } = data.creditApplication.financeInfo
    if (!externalBankOptions.length) {
      return
    }

    if (!externalBankOptions.some((option) => option && option.id === newBankAccountId)) {
      return
    }

    // we only want to select this bank account ID when no bank info has been
    // selected prior (i.e. manual bank account was linked first)
    const selectedBankAccount = data?.creditApplication?.financeInfo?.selectedBankInfo
    if (isBankInfoPopulated(selectedBankAccount)) {
      setNewBankAccountIdInternal(null)
      setIsLoadingNewAccount(false)
      return
    }

    selectConnection({
      variables: {
        creditApplicationId: appId ?? '',
        financeInfo: {
          selectedExternalId: newBankAccountId,
        },
      },
      onCompleted: () => {
        setNewBankAccountIdInternal(null)
        setIsLoadingNewAccount(false)
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully assigned new bank account as primary'
            id='sputnik.MXConnectionStatusProvider__+MLhYm'
          />,
        )
      },
      onError: () => {
        setNewBankAccountIdInternal(null)
        setIsLoadingNewAccount(false)
        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='There was an issue selecting your new bank account. Please try selecting again.'
            id='sputnik.MXConnectionStatusProvider__wp6kL5'
          />,
          { className: 'fs-unmask' },
        )
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, data, selectConnection])

  return (
    <MXConnectionStatusContext
      value={{
        isLoadingNewAccount,
        newBankAccountId,
        setNewBankAccountId,
      }}
    >
      {children}
    </MXConnectionStatusContext>
  )
}

export const useMXConnectionStatus = () => {
  const context = use(MXConnectionStatusContext)

  if (!context) {
    throw new Error('Cannot use MXConnectionStatusContext outside a MXConnectionStatusProvider')
  }

  return context
}

export default MXConnectionStatusProvider
